import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Dices, History, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Btn } from "@/components/common";

const DICE = [4, 6, 8, 10, 12, 20, 100];

export default function DiceRoller() {
  const qc = useQueryClient();
  const [notation, setNotation] = useState("1d20");
  const [modifier, setModifier] = useState(0);
  const [label, setLabel] = useState("");
  const [result, setResult] = useState(null);
  const [rolling, setRolling] = useState(false);

  const { data: history } = useQuery({
    queryKey: ["dice-history"],
    queryFn: async () => (await api.get("/dice/history")).data,
  });

  const roll = useMutation({
    mutationFn: async (note) => (await api.post("/dice/roll", { notation: note, label })).data,
    onSuccess: (data) => {
      setRolling(true);
      setTimeout(() => {
        setResult(data);
        setRolling(false);
        qc.invalidateQueries({ queryKey: ["dice-history"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      }, 500);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const quickRoll = (sides) => {
    const note = `1d${sides}${modifier ? (modifier > 0 ? `+${modifier}` : modifier) : ""}`;
    roll.mutate(note);
  };

  const customRoll = () => {
    const note = `${notation}${modifier ? (modifier > 0 ? `+${modifier}` : modifier) : ""}`;
    roll.mutate(note);
  };

  return (
    <div>
      <PageHeader title="Dice Roller" subtitle="Roll with advantage — history is saved" icon={Dices} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Result display */}
          <div className="glass rounded-3xl p-8 flex flex-col items-center justify-center min-h-[260px] relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none" />
            <AnimatePresence mode="wait">
              {rolling ? (
                <motion.div key="rolling" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Dices size={72} className="text-violet-400 animate-dice" />
                </motion.div>
              ) : result ? (
                <motion.div key={result.id} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                  <div className="font-mono text-sm text-zinc-500 mb-1">{result.notation}{result.label ? ` · ${result.label}` : ""}</div>
                  <div className="font-display text-7xl font-bold brand-gradient" data-testid="dice-total">{result.total}</div>
                  <div className="flex flex-wrap gap-1.5 justify-center mt-4">
                    {result.rolls.map((r, i) => (
                      <span key={i} className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${r === 20 ? "bg-teal-500/25 text-teal-300" : r === 1 ? "bg-rose-500/25 text-rose-300" : "bg-white/[0.06] text-zinc-300"}`}>{r}</span>
                    ))}
                    {result.modifier !== 0 && <span className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold bg-violet-500/20 text-violet-300">{result.modifier > 0 ? `+${result.modifier}` : result.modifier}</span>}
                  </div>
                </motion.div>
              ) : (
                <motion.div key="empty" className="text-center text-zinc-600">
                  <Sparkles size={48} className="mx-auto mb-3 text-zinc-700" />
                  <p>Tap a die to roll</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Quick dice */}
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2.5">
            {DICE.map((d) => (
              <button key={d} onClick={() => quickRoll(d)} data-testid={`die-d${d}`} className="glass glass-hover rounded-2xl py-4 flex flex-col items-center gap-1 group">
                <Dices size={22} className="text-violet-300 group-hover:scale-110 transition" />
                <span className="font-display font-bold text-sm">d{d}</span>
              </button>
            ))}
          </div>

          {/* Custom */}
          <div className="glass rounded-2xl p-4 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wide">Notation</label>
              <input value={notation} onChange={(e) => setNotation(e.target.value)} data-testid="dice-notation" placeholder="2d6" className="input-dark w-full rounded-xl px-3 py-2.5 text-sm font-mono mt-1" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wide">Modifier</label>
              <input type="number" value={modifier} onChange={(e) => setModifier(Number(e.target.value))} data-testid="dice-modifier" className="input-dark w-24 rounded-xl px-3 py-2.5 text-sm mt-1" />
            </div>
            <Btn onClick={customRoll} data-testid="custom-roll-btn"><Dices size={16} /> Roll</Btn>
          </div>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Attack, Stealth check)" className="input-dark w-full rounded-xl px-3 py-2.5 text-sm" data-testid="dice-label" />
        </div>

        {/* History */}
        <div>
          <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><History size={18} /> History</h2>
          <div className="glass rounded-3xl p-3 space-y-1.5 max-h-[600px] overflow-y-auto">
            {history?.length ? history.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-white/[0.03]" data-testid={`history-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-zinc-300">{r.notation}</div>
                  {r.label && <div className="text-xs text-zinc-600 truncate">{r.label}</div>}
                  <div className="text-[10px] text-zinc-700">[{r.rolls.join(", ")}]</div>
                </div>
                <span className="font-display font-bold text-xl text-violet-300">{r.total}</span>
              </div>
            )) : <p className="text-sm text-zinc-600 text-center py-8">No rolls yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
